import {
  AlertFillIcon, AlertIcon, ArrowLeftIcon, CalendarIcon, CheckCircleFillIcon, DownloadIcon, StopwatchIcon, SyncIcon, TerminalIcon, VerifiedIcon, WorkflowIcon,
  XCircleFillIcon,
} from "@primer/octicons-react";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, qs, repoPath, rp } from "../api/client";
import { useAction, useCi, useCiArtifacts, useCiLog, useRepoQueue, useWhoami } from "../api/hooks";
import { canIn, type CiOverview, type Evidence, type Verification } from "../api/types";
import { CopyButton, DescriptionList, Empty, ErrorMessage, Query, Sha, TimeAgo } from "../components/common";
import { bytes, duration, safeJson } from "../components/format";
import { Subhead } from "../components/SettingsLayout";
import { EvidenceIcon, StateLabel } from "../components/StateLabel";
import { Badge, type Tone } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { List, ListItem, ListItemMeta, ListItemSeparator, ListItemTitle } from "../components/ui/list";
import { Select } from "../components/ui/select";
import { Tooltip } from "../components/ui/tooltip";
import { cn } from "../lib/utils";
import { Changes } from "./Changes";
import { RepoQueue } from "./CiQueue";
import { runHref } from "./CiRun";

type Status = Evidence["status"];
type Job = NonNullable<CiOverview["config"]>["jobs"][number];

/** One commit's CI for one change (or one verification probe): the latest attempt of each job. */
interface Run {
  key: string;
  change_id: string;
  commit_sha: string;
  queued_at: number;
  jobs: Evidence[];
  status: Status;
}

const VERIFY_TONES: Record<string, Tone> = { passed: "green", running: "orange", reverted: "red", inconclusive: "yellow" };
const isVerify = (id: string) => id.startsWith("verify:");
const failed = (s: Status) => s === "fail" || s === "infra_error";
const active = (s: Status) => s === "queued" || s === "waiting";
/** A job, or a matrix job's instances (job_group). */
const belongs = (e: Evidence, name: string) => e.job === name || e.job_group === name;

/** A run in progress reads as in progress even if a job already failed. */
function rollup(jobs: Evidence[]): Status {
  if (jobs.some((e) => active(e.status))) return "queued";
  if (jobs.some((e) => failed(e.status))) return "fail";
  if (jobs.every((e) => e.status === "skipped")) return "skipped";
  return "pass";
}

/** Group evidence (newest first) into runs, keeping the newest attempt per job. */
function toRuns(recent: Evidence[]): Run[] {
  const runs = new Map<string, Run>();
  for (const e of recent) {
    const key = `${e.change_id}@${e.commit_sha}`;
    let r = runs.get(key);
    if (!r) runs.set(key, (r = { key, change_id: e.change_id, commit_sha: e.commit_sha, queued_at: e.queued_at, jobs: [], status: "pass" }));
    if (!r.jobs.some((j) => j.job === e.job)) r.jobs.push(e);
  }
  for (const r of runs.values()) {
    r.jobs.sort((a, b) => (a.needs?.length ?? 0) - (b.needs?.length ?? 0) || a.job.localeCompare(b.job));
    r.status = rollup(r.jobs);
  }
  return [...runs.values()];
}

const STATUS_FILTERS: Array<[string, string, (s: Status) => boolean]> = [
  ["", "All", () => true],
  ["failed", "Failed", failed],
  ["running", "In progress", active],
  ["passed", "Passed", (s) => s === "pass"],
];

export function Ci() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const q = useCi(repo);
  const queue = useRepoQueue(repo);
  const job = params.get("job") ?? "";
  // Changes is the default view; a job filter implies the runs view.
  const v = params.get("view");
  const view = v === "verify" || v === "queue" || v === "runs" ? v : job ? "runs" : "changes";
  const status = params.get("status") ?? "";
  return (
    <Query q={q}>
      {(d) => {
        const runs = toRuns(d.recent);
        const jobs = d.config?.jobs ?? [];
        const latest = (name: string) => {
          const mine = runs.find((r) => r.jobs.some((e) => belongs(e, name)))?.jobs.filter((e) => belongs(e, name));
          return mine?.length ? rollup(mine) : null;
        };
        return (
          <div>
            {view === "changes" ? (
              <Changes />
            ) : view === "queue" ? (
              queue.data ? <RepoQueue repo={repo} q={queue.data} /> : <Query q={queue}>{() => null}</Query>
            ) : view === "verify" ? (
              <VerifyView repo={repo} d={d} />
            ) : (
              <RunsView
                repo={repo}
                d={d}
                runs={runs}
                job={jobs.find((j) => j.name === job) ?? null}
                jobName={job}
                status={status}
                jobs={jobs}
                latest={latest}
                setFilter={(j, s) => setParams({ view: "runs", ...(j ? { job: j } : {}), ...(s ? { status: s } : {}) })}
              />
            )}
          </div>
        );
      }}
    </Query>
  );
}

function RunsView({ repo, d, runs, job, jobName, status, jobs, latest, setFilter }: {
  repo: string; d: CiOverview; runs: Run[]; job: Job | null; jobName: string; status: string; jobs: Job[]; latest: (name: string) => Status | null; setFilter: (job: string, status: string) => void;
}) {
  const scoped = jobName
    ? runs.flatMap((r) => {
        const mine = r.jobs.filter((e) => belongs(e, jobName));
        return mine.length ? [{ ...r, jobs: mine, status: rollup(mine) }] : [];
      })
    : runs;
  const match = STATUS_FILTERS.find(([id]) => id === status)?.[2] ?? (() => true);
  const shown = scoped.filter((r) => match(r.status));
  const instances = new Set(d.recent.filter((e) => e.job_group === jobName).map((e) => e.job));
  const total = d.totals.filter((t) => !jobName || t.job === jobName || instances.has(t.job));
  const n = total.reduce((s, t) => s + t.runs, 0);
  const pass = total.reduce((s, t) => s + t.pass, 0);
  return (
    <>
      <Subhead title={jobName || "All runs"} actions={!jobName && <VerifySummary repo={repo} d={d} />}>
        {n ? `${n} job run${n === 1 ? "" : "s"} · ${Math.round((pass / n) * 100)}% passing` : "No job runs yet"}
        {!jobName && d.flaky.length > 0 && ` · ${d.flaky.length} flaky`}
      </Subhead>
      {job && <JobCard repo={repo} job={job} flaky={d.flaky.find((f) => f.job === job.name)} />}
      <Card>
        <CardHeader>
          <span>
            {shown.length} {jobName ? "job run" : "run"}
            {shown.length === 1 ? "" : "s"}
          </span>
          {jobs.length > 0 && (
            <Select
              size="sm"
              align="end"
              className="ml-auto w-auto font-normal"
              prefix="Job:"
              value={jobName}
              onValueChange={(j) => setFilter(j, status)}
              options={[
                { value: "", label: "All" },
                ...jobs.map((j) => {
                  const st = latest(j.name);
                  const flaky = d.flaky.find((f) => f.job === j.name);
                  return {
                    value: j.name,
                    label: (
                      <span className="inline-flex items-center gap-2">
                        {st ? <EvidenceIcon status={st} /> : <span className="size-4 shrink-0 rounded-full border-2 border-dashed" />}
                        {j.name}
                        {flaky && <AlertIcon className="text-warning" aria-label={flaky.quarantined ? "quarantined" : "flaky"} />}
                      </span>
                    ),
                  };
                }),
              ]}
              aria-label="Filter by job"
            />
          )}
          <Select
            size="sm"
            align="end"
            className={cn("w-auto font-normal", !jobs.length && "ml-auto")}
            prefix="Status:"
            value={status}
            onValueChange={(s) => setFilter(jobName, s)}
            options={STATUS_FILTERS.map(([value, label]) => ({ value, label }))}
            aria-label="Filter by status"
          />
        </CardHeader>
        {shown.length ? (
          <List>
            {shown.map((r) => (
              <RunRow key={`${r.key}/${r.jobs[0]?.job}`} repo={repo} r={r} change={d.changes?.[r.change_id]} />
            ))}
          </List>
        ) : runs.length ? (
          <Empty icon={WorkflowIcon}>No runs match this filter.</Empty>
        ) : (
          <Empty icon={WorkflowIcon} bee="idle">
            No CI runs yet. Start a runner: <code>bee-runner --token &lt;runner token&gt;</code>
          </Empty>
        )}
      </Card>
    </>
  );
}

function RunRow({ repo, r, change }: { repo: string; r: Run; change?: CiOverview["changes"][string] }) {
  const verify = isVerify(r.change_id);
  return (
    <ListItem className="items-start">
      <span className="mt-[3px] inline-flex">
        <EvidenceIcon status={r.status} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <ListItemTitle asChild className="truncate">
            <Link to={runHref(repo, r.change_id, r.commit_sha)}>{verify ? "Post-land verification" : (change?.subject ?? `Change ${r.change_id.slice(-8)}`)}</Link>
          </ListItemTitle>
          {change && <StateLabel state={change.state} />}
        </div>
        <ListItemMeta>
          {verify ? <span>probe on main</span> : <Link className="font-mono text-muted-foreground" to={`${rp(repo)}/changes/${r.change_id}`}>#{r.change_id.slice(-8)}</Link>}
          <ListItemSeparator />
          <Sha repo={repo} sha={r.commit_sha} n={7} />
          {change && (
            <>
              <ListItemSeparator />
              <span>by {change.agent_id}</span>
            </>
          )}
        </ListItemMeta>
      </div>
      <div className="flex w-32 shrink-0 flex-col gap-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarIcon size={14} />
          <TimeAgo ms={r.queued_at} />
        </span>
        {wall(r) !== null && (
          <Tooltip content="From first job queued to last job finished">
            <span className="inline-flex items-center gap-1.5">
              <StopwatchIcon size={14} />
              {duration(wall(r))}
            </span>
          </Tooltip>
        )}
      </div>
    </ListItem>
  );
}

/** Approximate wall time of a finished run: first job queued to last job done. */
function wall(r: Run): number | null {
  if (r.jobs.some((e) => active(e.status) || e.ms === null)) return null;
  const start = Math.min(...r.jobs.map((e) => e.queued_at));
  return Math.max(...r.jobs.map((e) => e.queued_at + (e.ms ?? 0))) - start;
}

function JobCard({ repo, job, flaky }: { repo: string; job: Job; flaky?: CiOverview["flaky"][number] }) {
  const me = useWhoami();
  const act = useAction(repo);
  const admin = canIn(me.data, "admin", repo);
  return (
    <Card className="mt-0">
      <CardContent>
        <DescriptionList start>
          <dt>Command</dt>
          <dd>
            <code>{job.cmd}</code>
          </dd>
          <dt>Runs when</dt>
          <dd>
            {job.paths?.length ? <span className="font-mono">{job.paths.join(", ")}</span> : "any file changes"}
            {job.needs?.length ? <span className="text-muted-foreground">, or a job that needs it runs</span> : null}
          </dd>
          {job.needs?.length ? (
            <>
              <dt>Needs</dt>
              <dd className="flex flex-wrap items-center gap-1.5">
                {job.needs.map((n) => (
                  <Badge key={n} asChild>
                    <Link to={`${rp(repo)}/ci?job=${encodeURIComponent(n)}`}>{n}</Link>
                  </Badge>
                ))}
              </dd>
            </>
          ) : null}
          {job.matrix?.length ? (
            <>
              <dt>Matrix</dt>
              <dd>
                {job.matrix.map((a) => (
                  <div key={a.name}>
                    <code>BEE_MATRIX_{a.name.toUpperCase()}</code> <span className="font-mono">{a.values.join(", ")}</span>
                  </div>
                ))}
                <span className="text-muted-foreground">{job.matrix.reduce((n, a) => n * a.values.length, 1)} jobs per run</span>
              </dd>
            </>
          ) : null}
          <dt>Runner</dt>
          <dd>
            {job.instance}
            {job.image && <span className="text-muted-foreground"> · {job.image}</span>} <span className="text-muted-foreground">· {job.timeout}s timeout</span>
          </dd>
          {flaky && (
            <>
              <dt>Flaky</dt>
              <dd className="flex items-center gap-2">
                <Tooltip content={flaky.note}>
                  <Badge tone={flaky.quarantined ? "yellow" : "default"}>{flaky.quarantined ? "quarantined" : "flaky"}</Badge>
                </Tooltip>
                {flaky.quarantined ? "failures don't block landing" : `flagged ${flaky.flagged}×`}
                {admin && (
                  <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/admin/flaky`, body: { job: job.name, quarantined: !flaky.quarantined } })}>
                    {flaky.quarantined ? "Unquarantine" : "Quarantine"}
                  </Button>
                )}
              </dd>
            </>
          )}
        </DescriptionList>
        {act.error ? <ErrorMessage error={act.error} /> : null}
      </CardContent>
    </Card>
  );
}

function VerifyIcon({ state }: { state: string }) {
  switch (state) {
    case "passed":
      return <CheckCircleFillIcon className="text-success!" aria-label="passed" />;
    case "reverted":
      return <XCircleFillIcon className="text-danger!" aria-label="reverted" />;
    case "inconclusive":
      return <AlertFillIcon className="text-warning!" aria-label="inconclusive" />;
    default:
      return <SyncIcon className="animate-spin text-warning!" aria-label={state} />;
  }
}

/** A verification's `result` is JSON; spell out the common shapes instead of showing raw JSON. */
function verifyResult(v: Verification): string {
  if (v.state === "running") return `${v.phase}, probe ${v.probe}`;
  const r = safeJson(v.result);
  if (r && typeof r === "object") {
    const o = r as Record<string, unknown>;
    if (typeof o.verified_to === "number" && Object.keys(o).length === 1) return `verified through seq ${o.verified_to}`;
    return Object.entries(o).map(([k, val]) => `${k.replace(/_/g, " ")}: ${typeof val === "object" ? JSON.stringify(val) : String(val)}`).join(" · ");
  }
  return typeof r === "string" ? r : "";
}

function VerifySummary({ repo, d }: { repo: string; d: CiOverview }) {
  const v = d.verifications[0];
  return (
    <Button asChild size="sm" className="font-normal text-muted-foreground">
      <Link to={`${rp(repo)}/ci?view=verify`}>
        {v ? <VerifyIcon state={v.state} /> : <VerifiedIcon />}
        <span>
          main verified through seq <b className="text-foreground">{d.last_verified_seq}</b>
        </span>
      </Link>
    </Button>
  );
}

function VerifyView({ repo, d }: { repo: string; d: CiOverview }) {
  const me = useWhoami();
  const act = useAction(repo);
  const admin = canIn(me.data, "admin", repo);
  return (
    <>
      <Subhead
        title="Post-land verification"
        actions={
          admin && (
            <Button disabled={act.isPending} onClick={() => act.mutate({ path: `/v1/repos/${repo}/admin/verify`, body: {} })}>
              <VerifiedIcon /> Verify main now
            </Button>
          )
        }
      >
        After changes land, CI reruns on main to catch interactions between them. Main is verified through log seq <b>{d.last_verified_seq}</b>.
      </Subhead>
      {act.error ? <ErrorMessage error={act.error} /> : null}
      <Card className="mt-0">
        {d.verifications.length ? (
          <List>
            {d.verifications.map((v) => (
              <ListItem key={v.id} className="items-start">
                <span className="mt-[3px] inline-flex">
                  <VerifyIcon state={v.state} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ListItemTitle>
                      Log seq {v.from_seq + 1}–{v.to_seq}
                    </ListItemTitle>
                    <Badge tone={VERIFY_TONES[v.state] ?? "default"}>{v.state}</Badge>
                  </div>
                  <ListItemMeta>
                    <span className="font-mono">#{v.id}</span>
                    <ListItemSeparator />
                    <span>head</span>
                    <Sha repo={repo} sha={v.head_sha} n={7} />
                    {verifyResult(v) && (
                      <>
                        <ListItemSeparator />
                        <span>{verifyResult(v)}</span>
                      </>
                    )}
                  </ListItemMeta>
                </div>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  <TimeAgo ms={v.updated} />
                </span>
              </ListItem>
            ))}
          </List>
        ) : (
          <Empty icon={VerifiedIcon}>No verifications yet. One starts automatically after changes land.</Empty>
        )}
      </Card>
    </>
  );
}

const lineTone = (l: string) => (/\b(error|fail(ed|ure)?|panic|fatal)\b/i.test(l) ? "text-danger" : /\b(ok|pass(ed)?|success)\b/i.test(l) ? "text-success" : "");

export function CiLog() {
  const { repo = "" } = useParams();
  const [p] = useSearchParams();
  const change = p.get("change") ?? "";
  const job = p.get("job") ?? "";
  const attempt = Number(p.get("attempt") ?? 1);
  const q = useCiLog(repo, change, job, attempt);
  const live = q.data !== undefined && !q.data.complete;
  const art = useCiArtifacts(repo, change, job, attempt, q.data?.complete === true);
  const [dlError, setDlError] = useState<unknown>(null);
  const download = () =>
    api.download(repoPath(repo, `/ci/artifacts${qs({ change, job, attempt })}`), `${job}-${attempt}-artifacts.tar.gz`).then(() => setDlError(null), setDlError);
  return (
    // The page's first block, so it starts level with the side menu.
    <Card className="mt-0">
      <CardHeader>
        <TerminalIcon /> <b>{job}</b> <Badge>attempt {attempt}</Badge>
        {live && (
          <Tooltip content="Streaming from the runner; updates every few seconds">
            <Badge tone="orange">
              <SyncIcon /> live
            </Badge>
          </Tooltip>
        )}
        <span className="ml-auto flex items-center gap-2 font-normal">
          {art.data && (
            <Tooltip content={`${art.data.files} files, expires ${new Date(art.data.expires).toLocaleString()}`}>
              <Button size="sm" onClick={download}>
                <DownloadIcon /> Artifacts ({bytes(art.data.size)})
              </Button>
            </Tooltip>
          )}
          {q.data && <CopyButton value={q.data.text} title="Copy log" />}
          <Button asChild size="sm" variant="ghost">
            {isVerify(change) ? (
              <Link to={`${rp(repo)}/ci?view=verify`}>
                <ArrowLeftIcon /> Back to verification
              </Link>
            ) : (
              <Link to={`${rp(repo)}/changes/${change}`}>
                <ArrowLeftIcon /> Back to change
              </Link>
            )}
          </Button>
        </span>
      </CardHeader>
      {dlError ? <ErrorMessage error={dlError} /> : null}
      <Query q={q}>{({ text, complete }) => <LogLines text={text} complete={complete} />}</Query>
    </Card>
  );
}

/** A job log with line numbers and error/pass lines tinted. */
export function LogLines({ text, complete, className }: { text: string; complete: boolean; className?: string }) {
  return (
    <div className={cn("max-h-[72vh] overflow-auto bg-muted py-2 font-mono text-xs leading-5 text-foreground", className)}>
      {(text || (complete ? "(empty log)" : "Waiting for a runner to start this job…")).replace(/\n$/, "").split("\n").map((l, i) => (
        <div className="flex hover:bg-accent" key={i}>
          <span className="w-14 shrink-0 select-none pr-4 text-right text-faint">{i + 1}</span>
          <span className={cn("whitespace-pre-wrap break-all pr-4", lineTone(l))}>{l || " "}</span>
        </div>
      ))}
    </div>
  );
}
